import React, { ReactNode, useRef } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import * as S from './DesktopFilters.styles';
import { getDesktopFilterSelectOptionHref } from './DesktopFilterSelectOption.helpers';
import { DesktopFilterAccordion } from './DesktopFilterAccordion';
import { DesktopFilterSelectOption } from './DesktopFilterSelectOption';
import { FilterSelectOption } from '@/components/Filters/helpers.types';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { AccordionRef } from '@/components/UI/Accordion/Accordion';
import { QUERY } from '@/constants/common';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';

type Props = {
  isLoading?: boolean;
  dataTestId: string;
  queryParam: string;
  title: string;
  value: string | null;
  options: FilterSelectOption[];
  optionTestId: string;
  search?: ReactNode;
};

export const DesktopFilterSelectAccordion = ({
  isLoading,
  dataTestId,
  queryParam,
  title,
  value,
  options,
  optionTestId,
  search,
}: Props) => {
  const { clearUrl, queryString } = useUrlAndHashFromPath();
  const router = useRouter();
  const accordionRef = useRef<AccordionRef>(null);
  const { t } = useTranslation();

  const isDefaultSelected = !new URLSearchParams(queryString).get(
    `${QUERY.q}[${queryParam}]`
  );

  function handleClear() {
    const href = getDesktopFilterSelectOptionHref({
      clearUrl,
      value: undefined,
      queryParam,
      queryString,
    });
    router.push(href, undefined, { scroll: false });
  }

  if (isLoading) {
    return (
      <S.DesktopFilterLoaderContainer>
        <S.DesktopFilterLoaderRow>
          <SkeletonLoader width={80} height={20} borderRadius={7} />
          <SkeletonLoader width={20} height={20} borderRadius={7} />
        </S.DesktopFilterLoaderRow>
      </S.DesktopFilterLoaderContainer>
    );
  }

  return (
    <DesktopFilterAccordion
      ref={accordionRef}
      dataTestId={dataTestId}
      title={title}
      value={value}
      search={search}
      {...(!isDefaultSelected && { onClear: handleClear })}
    >
      {options.length > 0 ? (
        options.map(({ label, queryValue, isSelected }) => (
          <DesktopFilterSelectOption
            key={label}
            dataTestId={optionTestId}
            label={label}
            hrefParams={{
              clearUrl,
              value: queryValue,
              queryParam,
              queryString,
            }}
            isSelected={isSelected}
            onClick={() => accordionRef.current?.close()}
          />
        ))
      ) : (
        <S.NothingFound>{t('common.nothing_found')}</S.NothingFound>
      )}
    </DesktopFilterAccordion>
  );
};
